import { Bot, Mic, User } from 'lucide-react'
import type { Incident } from '../types/incident.types'
import { PriorityBadge } from './PriorityBadge'
import { formatRelative } from '../../../utils/formatters'

interface KanbanCardProps {
  incident: Incident
  onSelect?: (id: number) => void
}

function assigneeLabel(incident: Incident): string {
  if (incident.assignee_user_id) return `User #${incident.assignee_user_id}`
  if (incident.assignee_team_id) return `Team #${incident.assignee_team_id}`
  return 'Unassigned'
}

function SourceBadge({ incident }: { incident: Incident }): JSX.Element | null {
  if (incident.source === 'ai') {
    const pct = incident.confidence != null ? Math.round(incident.confidence * 100) : null
    return (
      <span className="inline-flex items-center gap-1 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
        <Bot className="h-2.5 w-2.5" aria-hidden="true" />
        AI{pct != null ? ` ${pct}%` : ''}
      </span>
    )
  }
  if (incident.source === 'VOICE') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
        <Mic className="h-2.5 w-2.5" aria-hidden="true" />
        Voice
      </span>
    )
  }
  return null
}

export function KanbanCard({ incident, onSelect }: KanbanCardProps): JSX.Element {
  const description = incident.description?.trim() ?? ''
  const snippet = description.length > 80 ? `${description.slice(0, 80)}…` : description
  const isNewAi = incident.status === 'NEW' && incident.source === 'ai'

  return (
    <button
      type="button"
      onClick={() => onSelect?.(incident.id)}
      disabled={!onSelect}
      className={`rounded-lg border bg-white p-3 text-left shadow-sm transition hover:shadow-md disabled:cursor-default ${
        isNewAi
          ? 'border-l-4 border-red-400 hover:border-red-500'
          : 'border-gray-200 hover:border-brand-green'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
          #{incident.id}
        </div>
        <SourceBadge incident={incident} />
      </div>
      <h4 className="mt-1 text-sm font-semibold text-gray-900">{incident.title}</h4>
      {snippet && (
        <p className="mt-1 text-xs text-gray-500 line-clamp-2">{snippet}</p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
          <User className="h-3 w-3" aria-hidden="true" />
          {assigneeLabel(incident)}
        </span>
        <PriorityBadge priority={incident.priority} />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
        <span className="text-xs text-gray-400">{formatRelative(incident.created_at)}</span>
      </div>
    </button>
  )
}
